import { z } from 'zod';
import { uuid } from './primitives';

/**
 * Usage / cost analytics wire contracts (Master Spec §6.5, §14). Shared between the
 * usage API (apps/server `usage/` module) and the web app's cost dashboard.
 *
 * The daily-cost breakdown powers a stacked bar chart: total cost (USD) per calendar
 * day for a rolling window, each day's bar segmented by the agent that drove that
 * portion of the cost. Cost is authoritative on `usage_records.cost_usd` (#195,
 * pricing-table based); the aggregation joins each record's run to its agent
 * (`usage_records.run_id` → `agent_runs.agent_id` → `agents.name`).
 *
 * Decisions baked into this shape:
 *  - Days are grouped in **UTC** (`date` is a `YYYY-MM-DD` UTC calendar day).
 *  - Quick-chat cost — chat turns back onto an `agent_runs` row with `agent_id = NULL`
 *    (the substrate, §21) — is NOT dropped: it folds into a single bucket with
 *    `agentId = null`, surfaced as its own **"Quick-chat"** segment so the daily
 *    totals are complete. Any other null-agent cost folds into the same bucket.
 *  - Every day in the window appears (zero-cost days have an all-zero `byAgent`), so
 *    the chart x-axis is a continuous range.
 */

/** A `YYYY-MM-DD` calendar day (UTC), as rendered on the chart's x-axis. */
export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/** One agent's cost slice within a single day. `agentId` null = the Quick-chat bucket. */
export const dailyCostAgentAmount = z.object({
  agentId: uuid.nullable(),
  agentName: z.string(),
  costUsd: z.number().nonnegative(),
});
export type DailyCostAgentAmount = z.infer<typeof dailyCostAgentAmount>;

/**
 * One day's costs. `byAgent` is ordered to match the top-level `agents` list (stable
 * stacking/colors across the window); `totalUsd` is the sum of the day's slices.
 */
export const dailyCostDay = z.object({
  date: isoDate,
  totalUsd: z.number().nonnegative(),
  byAgent: z.array(dailyCostAgentAmount),
});
export type DailyCostDay = z.infer<typeof dailyCostDay>;

/** A legend/series entry — a stable agent reference across the window. `id` null = Quick-chat. */
export const dailyCostAgentRef = z.object({
  id: uuid.nullable(),
  name: z.string(),
});
export type DailyCostAgentRef = z.infer<typeof dailyCostAgentRef>;

/**
 * The daily-cost-breakdown response (§14): a per-day row (with a per-agent slice
 * array) plus the ordered agent list for stable legend/series/color assignment. The
 * web renders one stacked bar per `days[]` entry, one series per `agents[]` entry.
 */
export const dailyCostBreakdown = z.object({
  /** The window length in days (echoes the effective, capped `days` query param). */
  windowDays: z.number().int().positive(),
  days: z.array(dailyCostDay),
  agents: z.array(dailyCostAgentRef),
});
export type DailyCostBreakdown = z.infer<typeof dailyCostBreakdown>;
